import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IApiError } from "../../../../../../@types/ApiError";
import { Notification } from "../../../../../../@types/Notification";
import { Button } from "../../../../../../components/Button";
import { workflowsService } from "../../../../../../services/worflowsService";
import { extractErrors } from "../../../../../../utils/apiErrorUtils";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { WorkflowPublishErrors } from "../../../WorkflowPublishErrors";
import {
  CancelButton,
  Description,
  ModalContainer,
  SubmitContainer,
  Title,
} from "./WorkflowStatusChangeModal.styles";

type WorkflowStatusChangeModalProps = {
  workflowId?: string;
  published: boolean;
  isWorkflowList?: boolean;
  onClose(
    notification?: Notification,
    refetchTable?: boolean,
    saveWorkflow?: boolean
  ): void;
};

export function WorkflowStatusChangeModal({
  workflowId,
  published,
  isWorkflowList,
  onClose,
}: WorkflowStatusChangeModalProps) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [publishErrors, setPublishErrors] = useState<IApiError[]>([]);

  mixpanelActions.track(
    `Visited Screen: ${published ? "Publish" : "Unpublish"} Modal`
  );

  const handleWorkflowStatusChange = async () => {
    if (!isWorkflowList || !workflowId) {
      onClose(undefined, undefined, true);
      return;
    }

    setPublishErrors([]);
    setIsLoading(true);

    const result = await workflowsService.updateWorkflowStatus(
      workflowId,
      published
    );

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    setIsLoading(false);

    if (result.status >= 200 && result.status < 300) {
      notification.message = t(
        published
          ? "workflows_toasts_published"
          : "workflows_toasts_unpublished"
      );

      onClose(notification, true);
      return;
    }

    // On publish the API validates the workflow against the target wards and
    // returns 400 with a list of incompatibilities. Surface them inline and
    // keep the modal open so the user knows what to fix before retrying.
    if (result.status === 400) {
      const errors = extractErrors(result);

      if (errors.length > 0) {
        setPublishErrors(errors);
        return;
      }
    }

    notification.message = t(
      published
        ? "workflows_toasts_publishFailed"
        : "workflows_toasts_unpublishFailed"
    );
    notification.type = "error";

    onClose(notification, true);
  };

  const handleModalDescriptionMessage = () => {
    if (workflowId && !isWorkflowList && published) {
      return t("workflows_statusModal_descriptionChanges");
    }

    if (!published) return t("workflows_statusModal_descriptionUnpublish");

    return t("workflows_statusModal_descriptionPublish");
  };

  return (
    <ModalContainer>
      <Title>
        {published
          ? t("workflows_statusModal_confirmPublish")
          : t("workflows_statusModal_confirmUnpublish")}
      </Title>
      <Description>{handleModalDescriptionMessage()}</Description>

      <WorkflowPublishErrors errors={publishErrors} />

      <SubmitContainer>
        <Button
          label={
            published
              ? t("workflows_statusModal_publish")
              : t("workflows_statusModal_unpublish")
          }
          type="submit"
          onClick={() => {
            handleWorkflowStatusChange();
            mixpanelActions.track(
              `User Action: ${
                published ? "Publish workflow" : "Unpublish workflow"
              }`
            );
          }}
          isLoading={isLoading}
        />

        <CancelButton
          onClick={() => {
            mixpanelActions.track(
              `User Action: Cancel ${published ? "publish" : "unpublish"} modal`
            );
            onClose();
          }}
          style={{ marginLeft: "-40px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
